import { Action } from "redux";

import { ReplyFormData } from "@/mk2/apps/bazaar/components/ReviewReplyForm";
import { ReviewRole } from "@/mk2/apps/bazaar/schemas";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";

export interface ReviewsLoadApiAction extends Action {
    readonly username: string;
    readonly after: number;
    readonly role: ReviewRole;
}

export const REVIEWS_LOAD_API_REQUEST = "REVIEWS_LOAD_API_REQUEST";

export interface ReviewsLoadApiRequestAction extends ReviewsLoadApiAction, APIRequestAction {
    readonly type: typeof REVIEWS_LOAD_API_REQUEST;
}

export interface ReviewsLoadNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
        reviews: number[];
    };
}

export const REVIEWS_LOAD_API_SUCCESS = "REVIEWS_LOAD_API_SUCCESS";

export interface ReviewsLoadApiSuccessAction
    extends ReviewsLoadApiAction, APISuccessAction<ReviewsLoadNormalizedResponse> {
    readonly type: typeof REVIEWS_LOAD_API_SUCCESS;
    readonly hasNext: number;
    readonly hasPrevious: number;
}

export const REVIEWS_LOAD_API_FAILURE = "REVIEWS_LOAD_API_FAILURE";

export interface ReviewsLoadApiFailureAction extends ReviewsLoadApiAction, APIFailureAction {
    readonly type: typeof REVIEWS_LOAD_API_FAILURE;
}

export const reviewsLoadApi = {
    request: (username: string, role: ReviewRole, after: number): ReviewsLoadApiRequestAction => ({
        type: REVIEWS_LOAD_API_REQUEST,
        username,
        role,
        after,
    }),
    success: (
        username: string,
        role: ReviewRole,
        after: number,
        hasNext: number,
        hasPrevious: number,
        response: ReviewsLoadNormalizedResponse,
    ): ReviewsLoadApiSuccessAction => ({
        type: REVIEWS_LOAD_API_SUCCESS,
        username,
        role,
        after,
        hasNext,
        hasPrevious,
        response,
    }),
    failure: (
        username: string,
        role: ReviewRole,
        after: number,
        error: string,
    ): ReviewsLoadApiFailureAction => ({
        type: REVIEWS_LOAD_API_FAILURE,
        username,
        role,
        after,
        error,
    }),
};

export const REVIEWS_LOAD = "REVIEWS_LOAD";

export interface ReviewsLoadAction extends ReviewsLoadApiAction {
    readonly type: typeof REVIEWS_LOAD;
}

export const reviewsLoad = (
    username: string,
    role: ReviewRole,
    after?: number,
): ReviewsLoadAction => ({
    type: REVIEWS_LOAD,
    username,
    role,
    after,
});

export interface ReviewsAddReplyApiAction extends Action {
    readonly reviewId: number;
    readonly reply: ReplyFormData;
}

export const REVIEWS_ADD_REPLY_API_REQUEST = "REVIEWS_ADD_REPLY_API_REQUEST";

export interface ReviewsAddReplyApiRequestAction
    extends ReviewsAddReplyApiAction, APIRequestAction {
    readonly type: typeof REVIEWS_ADD_REPLY_API_REQUEST;
}

export interface ReviewsAddReplyNormalizedResponse extends NormalizedResponse {
    readonly result: {
        review: number;
    };
}

export const REVIEWS_ADD_REPLY_API_SUCCESS = "REVIEWS_ADD_REPLY_API_SUCCESS";

export interface ReviewsAddReplyApiSuccessAction
    extends ReviewsAddReplyApiAction, APISuccessAction<ReviewsAddReplyNormalizedResponse> {
    readonly type: typeof REVIEWS_ADD_REPLY_API_SUCCESS;
}

export const REVIEWS_ADD_REPLY_API_FAILURE = "REVIEWS_ADD_REPLY_API_FAILURE";

export interface ReviewsAddReplyApiFailureAction
    extends ReviewsAddReplyApiAction, APIFailureAction {
    readonly type: typeof REVIEWS_ADD_REPLY_API_FAILURE;
}

export const reviewsAddReplyApi = {
    request: (reviewId: number, reply: ReplyFormData): ReviewsAddReplyApiRequestAction => ({
        type: REVIEWS_ADD_REPLY_API_REQUEST,
        reviewId,
        reply,
    }),
    success: (
        reviewId: number,
        reply: ReplyFormData,
        response: ReviewsAddReplyNormalizedResponse,
    ): ReviewsAddReplyApiSuccessAction => ({
        type: REVIEWS_ADD_REPLY_API_SUCCESS,
        reviewId,
        reply,
        response,
    }),
    failure: (
        reviewId: number,
        reply: ReplyFormData,
        error: string,
    ): ReviewsAddReplyApiFailureAction => ({
        type: REVIEWS_ADD_REPLY_API_FAILURE,
        reviewId,
        reply,
        error,
    }),
};

export const REVIEWS_ADD_REPLY = "REVIEWS_ADD_REPLY";

export interface ReviewsAddReplyAction extends ReviewsAddReplyApiAction {
    readonly type: typeof REVIEWS_ADD_REPLY;
}

export const reviewsAddReply = (reviewId: number, reply: ReplyFormData): ReviewsAddReplyAction => ({
    type: REVIEWS_ADD_REPLY,
    reviewId,
    reply,
});

export interface ReviewsDeleteReplyApiAction extends Action {
    readonly reviewId: number;
}

export const REVIEWS_DELETE_REPLY_API_REQUEST = "REVIEWS_DELETE_REPLY_API_REQUEST";

export interface ReviewsDeleteReplyApiRequestAction
    extends ReviewsDeleteReplyApiAction, APIRequestAction {
    readonly type: typeof REVIEWS_DELETE_REPLY_API_REQUEST;
}

export interface ReviewsDeleteReplyNormalizedResponse extends NormalizedResponse {
    readonly result: {
        review: number;
    };
}

export const REVIEWS_DELETE_REPLY_API_SUCCESS = "REVIEWS_DELETE_REPLY_API_SUCCESS";

export interface ReviewsDeleteReplyApiSuccessAction
    extends ReviewsDeleteReplyApiAction, APISuccessAction<ReviewsDeleteReplyNormalizedResponse> {
    readonly type: typeof REVIEWS_DELETE_REPLY_API_SUCCESS;
}

export const REVIEWS_DELETE_REPLY_API_FAILURE = "REVIEWS_DELETE_REPLY_API_FAILURE";

export interface ReviewsDeleteReplyApiFailureAction
    extends ReviewsDeleteReplyApiAction, APIFailureAction {
    readonly type: typeof REVIEWS_DELETE_REPLY_API_FAILURE;
}

export const reviewsDeleteReplyApi = {
    request: (reviewId: number): ReviewsDeleteReplyApiRequestAction => ({
        type: REVIEWS_DELETE_REPLY_API_REQUEST,
        reviewId,
    }),
    success: (
        reviewId: number,
        response: ReviewsDeleteReplyNormalizedResponse,
    ): ReviewsDeleteReplyApiSuccessAction => ({
        type: REVIEWS_DELETE_REPLY_API_SUCCESS,
        reviewId,
        response,
    }),
    failure: (reviewId: number, error: string): ReviewsDeleteReplyApiFailureAction => ({
        type: REVIEWS_DELETE_REPLY_API_FAILURE,
        reviewId,
        error,
    }),
};

export const REVIEWS_DELETE_REPLY = "REVIEWS_DELETE_REPLY";

export interface ReviewsDeleteReplyAction extends ReviewsDeleteReplyApiAction {
    readonly type: typeof REVIEWS_DELETE_REPLY;
}

export const reviewsDeleteReply = (reviewId: number): ReviewsDeleteReplyAction => ({
    type: REVIEWS_DELETE_REPLY,
    reviewId,
});

export interface ReviewsDeleteApiAction extends Action {
    readonly reviewId: number;
    readonly username: string;
    readonly role: ReviewRole;
}

export const REVIEWS_DELETE_API_REQUEST = "REVIEWS_DELETE_API_REQUEST";

export interface ReviewsDeleteApiRequestAction extends ReviewsDeleteApiAction, APIRequestAction {
    readonly type: typeof REVIEWS_DELETE_API_REQUEST;
}

export interface ReviewsDeleteNormalizedResponse extends NormalizedResponse {
    readonly result: {
        review: number;
        seller: number;
    };
}

export const REVIEWS_DELETE_API_SUCCESS = "REVIEWS_DELETE_API_SUCCESS";

export interface ReviewsDeleteApiSuccessAction
    extends ReviewsDeleteApiAction, APISuccessAction<ReviewsDeleteNormalizedResponse> {
    readonly type: typeof REVIEWS_DELETE_API_SUCCESS;
}

export const REVIEWS_DELETE_API_FAILURE = "REVIEWS_DELETE_API_FAILURE";

export interface ReviewsDeleteApiFailureAction extends ReviewsDeleteApiAction, APIFailureAction {
    readonly type: typeof REVIEWS_DELETE_API_FAILURE;
}

export const reviewsDeleteApi = {
    request: (
        username: string,
        role: ReviewRole,
        reviewId: number,
    ): ReviewsDeleteApiRequestAction => ({
        type: REVIEWS_DELETE_API_REQUEST,
        username,
        reviewId,
        role,
    }),
    success: (
        username: string,
        role: ReviewRole,
        reviewId: number,
        response: ReviewsDeleteNormalizedResponse,
    ): ReviewsDeleteApiSuccessAction => ({
        type: REVIEWS_DELETE_API_SUCCESS,
        username,
        reviewId,
        role,
        response,
    }),
    failure: (
        username: string,
        role: ReviewRole,
        reviewId: number,
        error: string,
    ): ReviewsDeleteApiFailureAction => ({
        type: REVIEWS_DELETE_API_FAILURE,
        username,
        reviewId,
        role,
        error,
    }),
};

export const REVIEWS_DELETE = "REVIEWS_DELETE";

export interface ReviewsDeleteAction extends ReviewsDeleteApiAction {
    readonly type: typeof REVIEWS_DELETE;
}

export const reviewsDelete = (
    username: string,
    role: ReviewRole,
    reviewId: number,
): ReviewsDeleteAction => ({
    type: REVIEWS_DELETE,
    username,
    reviewId,
    role,
});

export interface ReviewsReadApiAction extends Action {
    readonly username: string;
    readonly role: ReviewRole;
    readonly reviewsIds: number[];
}

export const REVIEWS_READ_API_REQUEST = "REVIEWS_READ_API_REQUEST";

export interface ReviewsReadApiRequestAction extends ReviewsReadApiAction, APIRequestAction {
    readonly type: typeof REVIEWS_READ_API_REQUEST;
}

export const REVIEWS_READ_API_SUCCESS = "REVIEWS_READ_API_SUCCESS";

export interface ReviewsReadApiSuccessAction extends ReviewsReadApiAction, APISuccessAction {
    readonly type: typeof REVIEWS_READ_API_SUCCESS;
}

export const REVIEWS_READ_API_FAILURE = "REVIEWS_READ_API_FAILURE";

export interface ReviewsReadApiFailureAction extends ReviewsReadApiAction, APIFailureAction {
    readonly type: typeof REVIEWS_READ_API_FAILURE;
}

export const reviewsReadApi = {
    request: (
        username: string,
        role: ReviewRole,
        reviewsIds: number[],
    ): ReviewsReadApiRequestAction => ({
        type: REVIEWS_READ_API_REQUEST,
        username,
        role,
        reviewsIds,
    }),
    success: (
        username: string,
        role: ReviewRole,
        reviewsIds: number[],
        response: NormalizedResponse,
    ): ReviewsReadApiSuccessAction => ({
        type: REVIEWS_READ_API_SUCCESS,
        username,
        role,
        reviewsIds,
        response,
    }),
    failure: (
        username: string,
        role: ReviewRole,
        reviewsIds: number[],
        error: string,
    ): ReviewsReadApiFailureAction => ({
        type: REVIEWS_READ_API_FAILURE,
        username,
        role,
        reviewsIds,
        error,
    }),
};

export const REVIEWS_READ = "REVIEWS_READ";

export interface ReviewsReadAction extends ReviewsReadApiAction {
    readonly type: typeof REVIEWS_READ;
}

export const reviewsRead = (
    username: string,
    role: ReviewRole,
    reviewsIds: number[],
): ReviewsReadAction => ({
    type: REVIEWS_READ,
    username,
    role,
    reviewsIds,
});
